import { useRef, useState } from 'react';
import { ApiError, getProfile, putProfile } from '../core/api';
import { useT } from '../core/lexicon';
import { useAsync } from '../core/useAsync';
import { SpriteFace } from '../core/sprite';
import { useApp } from '../App';
import { Screen, TopBar, CenterNote } from './Layout';
import type { MeProfile } from '../core/types';

const N = 12;
type Ch = '.' | '1' | '2' | '3';
const PALETTE: { ch: Ch; label: string }[] = [
  { ch: '.', label: 'RUB' },
  { ch: '1', label: 'LINE' },
  { ch: '2', label: 'SHADE' },
  { ch: '3', label: 'SKIN' },
];
const COLS: Record<Ch, string> = { '.': 'var(--k3)', '1': 'var(--k0)', '2': 'var(--k1)', '3': 'var(--k3)' };

const STARTERS: Record<string, string[]> = {
  face: ['............', '...111111...', '..11111111..', '..13333331..', '..11311311..', '..13333331..', '..13222231..', '..13222231..', '...133331...', '....1111....', '..11333311..', '.1313131313.'],
  beard: ['............', '...111111...', '..11111111..', '..13333331..', '..13133131..', '..13333331..', '..12222221..', '..12222221..', '...122221...', '....1111....', '..11333311..', '.1313131313.'],
  cap: ['............', '...111111...', '..11111111..', '111111111111', '..13333331..', '..13133131..', '..13333331..', '..13322331..', '...133331...', '....1111....', '..11333311..', '.1313131313.'],
  cans: ['............', '...111111...', '..11111111..', '111333333111', '111313313111', '111333333111', '..13333331..', '..13322331..', '...133331...', '....1111....', '..11333311..', '.1313131313.'],
};

const blank = (): string[] => Array.from({ length: N }, () => '.'.repeat(N));

/** Load the caller's profile (read side of PUT; may 404 on branches without the
 *  endpoint yet — treated as empty so About Me still works save-only). */
export function About() {
  const t = useT();
  const prof = useAsync<MeProfile>(async () => {
    try {
      return await getProfile();
    } catch (e) {
      if ((e as ApiError).status === 404) return { bio: '', phrases: [], sprite_data: null };
      throw e;
    }
  }, []);

  if (prof.loading) {
    return (
      <Screen>
        <TopBar title={t('profile.name')} />
        <CenterNote>
          <div className="spin">LOADING…</div>
        </CenterNote>
      </Screen>
    );
  }
  if (prof.error || !prof.data) {
    return (
      <Screen>
        <TopBar title={t('profile.name')} />
        <CenterNote title="SOMETHING BROKE">
          <p className="errline">{prof.error?.message}</p>
        </CenterNote>
      </Screen>
    );
  }
  return <Editor initial={prof.data} />;
}

function Editor({ initial }: { initial: MeProfile }) {
  const t = useT();
  const { user } = useApp();

  const [px, setPx] = useState<string[][]>(
    (initial.sprite_data ?? STARTERS.face).map((r) => [...r]),
  );
  const [pen, setPen] = useState<Ch>('1');
  const [bio, setBio] = useState(initial.bio ?? '');
  const [phrases, setPhrases] = useState<string[]>(initial.phrases ?? []);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const history = useRef<string[][][]>([]);
  const painting = useRef(false);

  const rows = px.map((r) => r.join(''));

  const snap = () => {
    history.current.push(px.map((r) => r.slice()));
    if (history.current.length > 50) history.current.shift();
  };
  const paint = (x: number, y: number) => {
    setPx((prev) => {
      const next = prev.map((r) => r.slice());
      next[y][x] = pen;
      return next;
    });
    setDirty(true);
  };

  const addPhrase = () => {
    if (phrases.length >= 5) return;
    setPhrases((p) => [...p, '']);
    setDirty(true);
  };

  async function save() {
    setBusy(true);
    setProblem(null);
    try {
      await putProfile({ bio, phrases: phrases.filter((p) => p.trim().length > 0), sprite_data: rows });
      setDirty(false);
      setSaved(t('profile.saved'));
      window.setTimeout(() => setSaved(''), 3000);
    } catch (e) {
      setProblem((e as ApiError).message || 'Could not save.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen>
      <TopBar title={t('profile.name')} sub={user.display_name.toUpperCase()} right={user.display_name.toUpperCase()} />

      <div className="body-scroll">
        <section className="block">
          <h2>{t('profile.seen_by').toUpperCase()}</h2>
          <div className="who-strip">
            <SpriteFace rows={rows} size={60} />
            <div className="name">{user.display_name.toUpperCase()}</div>
          </div>
          <p className="hint">{t('profile.face.hint')}</p>
        </section>

        <section className="block">
          <h2>{t('profile.bio.label').toUpperCase()}</h2>
          <p className="hint">{t('profile.bio.hint')}</p>
          <textarea
            className="bio-area"
            maxLength={200}
            value={bio}
            placeholder={t('profile.no_bio')}
            onChange={(e) => {
              setBio(e.target.value);
              setDirty(true);
            }}
          />
          <div className={`count${bio.length > 180 ? ' over' : ''}`}>{bio.length}/200</div>
        </section>

        <section className="block">
          <h2>{t('profile.phrases.label').toUpperCase()}</h2>
          <p className="hint">{t('profile.phrases.hint')}</p>
          {phrases.length === 0 ? <p className="pempty">{t('profile.phrases.empty')}</p> : null}
          {phrases.map((v, i) => (
            <div className="pline" key={i}>
              <input
                value={v}
                maxLength={120}
                placeholder={t('profile.phrases.label')}
                onChange={(e) => {
                  setPhrases((p) => p.map((x, j) => (j === i ? e.target.value : x)));
                  setDirty(true);
                }}
              />
              <button
                onClick={() => {
                  setPhrases((p) => p.filter((_, j) => j !== i));
                  setDirty(true);
                }}
              >
                X
              </button>
            </div>
          ))}
          <button className="tool" style={{ width: '100%', marginTop: 10 }} disabled={phrases.length >= 5} onClick={addPhrase}>
            {phrases.length >= 5 ? 'THAT IS FIVE' : 'ADD A LINE'}
          </button>
        </section>

        <section className="block">
          <h2>{t('profile.face.label').toUpperCase()}</h2>
          <p className="hint">{t('profile.face.hint')}</p>
          <div
            id="grid"
            onPointerDown={(e) => {
              const cell = (e.target as HTMLElement).closest('[data-x]') as HTMLElement | null;
              if (!cell) return;
              snap();
              painting.current = true;
              paint(Number(cell.dataset.x), Number(cell.dataset.y));
            }}
            onPointerOver={(e) => {
              if (!painting.current) return;
              const cell = (e.target as HTMLElement).closest('[data-x]') as HTMLElement | null;
              if (cell) paint(Number(cell.dataset.x), Number(cell.dataset.y));
            }}
            onPointerUp={() => (painting.current = false)}
            onPointerLeave={() => (painting.current = false)}
          >
            {px.map((r, y) =>
              r.map((ch, x) => (
                <div key={`${x}-${y}`} data-x={x} data-y={y} style={{ background: COLS[ch as Ch] }} />
              )),
            )}
          </div>

          <div className="pal">
            {PALETTE.map((p) => (
              <button
                key={p.ch}
                className={pen === p.ch ? 'on' : ''}
                style={{ background: COLS[p.ch] }}
                onClick={() => setPen(p.ch)}
              >
                <span>{p.label}</span>
              </button>
            ))}
          </div>

          <div className="tools">
            <button
              className="tool"
              onClick={() => {
                snap();
                setPx((prev) => prev.map((row) => row.map((_, x) => (x < N / 2 ? row[x] : row[N - 1 - x]))));
                setDirty(true);
              }}
            >
              MIRROR
            </button>
            <button
              className="tool"
              onClick={() => {
                const last = history.current.pop();
                if (last) setPx(last);
              }}
            >
              UNDO
            </button>
            <button
              className="tool"
              onClick={() => {
                snap();
                setPx(blank().map((r) => [...r]));
                setDirty(true);
              }}
            >
              CLEAR
            </button>
          </div>

          <div className="starters">
            {Object.entries(STARTERS).map(([k, s]) => (
              <button
                key={k}
                onClick={() => {
                  snap();
                  setPx(s.map((r) => [...r]));
                  setDirty(true);
                }}
              >
                <SpriteFace rows={s} size={36} />
              </button>
            ))}
          </div>

          <div className="prevs">
            {[[36, 'in a list'], [48, 'in the line-up'], [60, 'on the reveal']].map(([sz, cap]) => (
              <div className="prev" key={cap}>
                <SpriteFace rows={rows} size={sz as number} />
                <div className="cap">{cap as string}</div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="act">
        {problem ? <div className="errline" style={{ marginBottom: 8 }}>{problem}</div> : null}
        <button className="go" disabled={!dirty || busy} onClick={save}>
          {busy ? '…' : 'SAVE'}
        </button>
        <div className="saved">{saved}</div>
      </div>
    </Screen>
  );
}
